import { Button, Paper, Tooltip } from '@mui/material';
import {
    FormInputGroup,
    FormProvider,
    FormSelect,
    FormSubmitButton,
    FormText,
} from 'components/form';
import { addHours, format, startOfToday } from 'date-fns';
import { Device } from 'types';
import * as yup from 'yup';
import { FormSelectOption } from 'components/form/types';
import styles from './DeviceTests.module.scss';
import { HelpOutline } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import { FieldValues } from 'react-hook-form';
import { getObjectDiff } from 'helpers/general';
import { useLazyPatchUpdateDeviceQuery } from 'store/services';
import Save from '@mui/icons-material/Save';
import { useState } from 'react';

interface DeviceTestProps {
    device: Device;
    setDevice: (d: Device) => void;
}

interface DeviceTestForm {
    cov: number;
    e_int: number;
    ftt: number;
    f_cnt: number;
    f_int: number;
    r_int: number;
    thresh1: number;
    thresh2: number;
    t_int: number;
}

const fixedReportTimeOpts: FormSelectOption[] = [
    { name: 'Default', value: -1 },
];
const midnight = startOfToday();

for (let i = 0; i < 24; i++) {
    const val = i * 3600;
    const hour = addHours(midnight, i);
    const hourDisplay = format(hour, 'hh:mm a');
    fixedReportTimeOpts.push({ name: hourDisplay, value: val });
}

const testsSchema = yup.object<{
    [k in keyof DeviceTestForm]: yup.AnySchema;
}>({
    cov: yup.number().nullable(),
    e_int: yup.number().nullable(),
    ftt: yup.number().nullable(),
    f_cnt: yup.number().nullable(),
    f_int: yup.number().nullable(),
    r_int: yup.number().nullable(),
    thresh1: yup.number().nullable(),
    thresh2: yup.number().nullable(),
    t_int: yup.number().nullable(),
});

export const DeviceTests = ({ device, setDevice }: DeviceTestProps) => {
    const dispatch = useDispatch();

    const [isSaving, setIsSaving] = useState(false);

    const [updateDeviceQuery] = useLazyPatchUpdateDeviceQuery();

    const defaultValues = {
        cov: device.cov ?? -1,
        e_int: -1,
        ftt: -1,
        r_int: device.r_int ?? 6 * 60 * 60,
        thresh1: device.thresh1 ?? -1,
        thresh2: device.thresh2 ?? -1,
        t_int: device.t_int ?? 24 * 60 * 60,
    };

    const tooltips = {
        cov: 'The measured change in the tank level % that will trigger an additional reading. The monitor will also send a reading every 24 hours in addition to when the threshold is exceeded.',
        rint: 'The number of hours between comparison to the Change of Tank Level threshold measurements. For example, Reading Interval of 1 hour, will check the tank level 24 times per day, but only send a tank level update when a change is greater than the Change of Tank Level threshold.',
        tint: 'The number of hours between readings being updated in the portal. For example, Reporting Interval of 12 hours, will send two readings per day.',
        ftt: 'Time of day that tank levels will update. Only one reading will be sent every 24 hours.',
        thresh: 'If tank level drops below this % it will trigger an additional reading. The monitor will also send a reading every 24  hours in addition to when the threshold is exceeded.',
    };

    const onSubmit = (data: FieldValues) => {
        setIsSaving(true);
        const newData = getObjectDiff(defaultValues, data);

        updateDeviceQuery({
            deviceId: device.deviceId,
            updatedFields: newData,
        })
            .then((result) => {
                if (result?.data) {
                    setIsSaving(false);
                    setDevice(result.data);
                    dispatch(
                        activateSnackbar({
                            message:
                                'Test config saved. Changes take effect at next reading.',
                            severity: 'success',
                        })
                    );
                }
            })
            .catch(() => {
                setIsSaving(false);
            });
    };

    const onResetDefaults = () => {
        const defaults = {
            cov: -1,
            e_int: -1,
            ftt: -1,
            r_int: 6 * 60 * 60,
            thresh1: -1,
            thresh2: -1,
            t_int: 24 * 60 * 60,
        };

        onSubmit(defaults);
    };

    return (
        <div data-testid="DeviceTests" className={styles.container}>
            <Paper className={styles.formSheet}>
                <FormProvider
                    defaultValues={defaultValues}
                    validationSchema={testsSchema}
                >
                    <FormInputGroup>
                        <div className={styles.testInput}>
                            <FormText
                                field="cov"
                                label="Change of Tank Level Since Last Reading"
                                isNumber
                            />
                            <Tooltip title={tooltips['cov']}>
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                        <div className={styles.testInput}>
                            <FormText
                                field="r_int"
                                label="Reading Interval"
                                isNumber
                            />
                            <Tooltip title={tooltips['rint']}>
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                    </FormInputGroup>
                    <FormInputGroup>
                        <div className={styles.testInput}>
                            <FormText
                                field="thresh1"
                                label="Thresh 1"
                                isNumber
                            />
                            <Tooltip title={tooltips['thresh']}>
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                        <div className={styles.testInput}>
                            <FormSelect
                                field="ftt"
                                label="Fixed Reporting Time"
                                options={fixedReportTimeOpts}
                            />
                            <Tooltip title={tooltips['ftt']}>
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                    </FormInputGroup>
                    <FormInputGroup>
                        <div className={styles.testInput}>
                            <FormText
                                field="thresh2"
                                label="Thresh 2"
                                isNumber
                            />
                            <Tooltip title={tooltips['thresh']}>
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                        <div className={styles.testInput}>
                            <FormText
                                field="f_int"
                                label="Fail Interval"
                                isNumber
                            />
                        </div>
                    </FormInputGroup>
                    <FormInputGroup>
                        <div className={styles.testInput}>
                            <FormText
                                field="e_int"
                                label="Error Interval"
                                isNumber
                            />
                        </div>
                        <div className={styles.testInput}>
                            <FormText
                                field="f_cnt"
                                label="Fail Count"
                                isNumber
                            />
                        </div>
                    </FormInputGroup>
                    <FormInputGroup>
                        <div className={styles.testInput}>
                            <FormText
                                field="t_int"
                                label="Reporting Interval"
                                isNumber
                            />
                            <Tooltip title={tooltips['tint']}>
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                    </FormInputGroup>
                    <div className={styles.formFooter}>
                        <Button onClick={onResetDefaults}>
                            Reset Defaults
                        </Button>
                        <FormSubmitButton
                            onValidHandler={onSubmit}
                            onErrorHandler={() => {
                                setIsSaving(false);
                                dispatch(
                                    activateSnackbar({
                                        message:
                                            'Something went wrong, please check your responses.',
                                        severity: 'error',
                                    })
                                );
                            }}
                            buttonLabel="Save"
                            startIcon={<Save />}
                            isLoading={isSaving}
                        />
                    </div>
                </FormProvider>
            </Paper>
        </div>
    );
};
